import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Home, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';

export default function ResetPassword() {
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      showToast({
        type: 'error',
        message: 'Please enter your email address'
      });
      return;
    }

    try {
      setIsLoading(true);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/update-password`
      });

      if (error) throw error;

      setResetSent(true);
      showToast({
        type: 'success',
        message: 'Password reset email sent!'
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to send reset email'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center relative">
      <Link
        to="/"
        className="absolute top-8 left-8 flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
      >
        <Home className="w-5 h-5" />
        <span>Return Home</span>
      </Link>

      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-lg">
        {resetSent ? (
          // Success state
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">Check Your Email</h2>
              <p className="text-gray-600">
                We've sent a password reset link to
              </p>
              <p className="text-blue-600 font-medium mt-1">{email}</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-700">
                  <p className="font-medium text-blue-900 mb-2">Next steps:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Check your inbox for the password reset email</li>
                    <li>Click the link in the email</li>
                    <li>Enter your new password</li>
                    <li>Sign in with your new password</li>
                  </ol>
                  <p className="mt-3 text-gray-600">
                    <strong>Note:</strong> The link will expire in 1 hour.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 space-y-3">
              <Link to="/login">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 py-3 rounded-lg text-white">
                  Back to Login
                </Button>
              </Link>
              <button
                onClick={() => setResetSent(false)}
                className="w-full text-sm text-gray-600 hover:text-blue-600"
              >
                Send to a different email
              </button>
            </div>
          </div>
        ) : (
          // Reset form
          <>
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-2">Reset Password</h2>
              <p className="text-gray-600">
                Enter your email address and we'll send you a link to reset your password
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 py-3 rounded-lg text-white"
                loading={isLoading}
              >
                Send Reset Link
              </Button>
            </form>

            <div className="text-center space-y-2">
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-blue-600"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/signup" className="text-blue-600 hover:text-blue-700 font-medium">
                  Sign up
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
